import { useState, type ReactNode } from 'react'

import type { RecipeV2Doc, RecipeV2Summary } from '@ctcells/api'
import { intlLocale, t, type MessageKey } from '@ctcells/i18n'

import { DataTable } from '../../atoms/DataTable.tsx'
import { Disclosure } from '../../atoms/Disclosure.tsx'
import { ValueRows } from '../../atoms/ValueRows.tsx'
import { DeckRackFigure } from '../../figures/DeckRackFigure.tsx'
import { AssemblerStationLine } from '../../figures/LineFigures.tsx'
import { RotorFigure } from '../../figures/RotorFigure.tsx'
import { REST_CELLS } from '../../figures/deckRackDefinitions.ts'
import { TrayFigure } from '../../figures/TrayFigure.tsx'
import { deckRacksOf, sameLabware } from '../monitoring/deckRacks.ts'
import { TRAY_SLOTS } from '../monitoring/monitoringModel.ts'
import { RackAxesFrame } from '../monitoring/SupplyDetail.tsx'

import { RecipeDeckFigure } from './RecipeDeckFigure.tsx'
import { Card } from '../resourceParts.tsx'
import {
  fieldId,
  defaultValue,
  fieldsOf,
  initialValues,
  moduleStepTable,
  reagentKey,
  stageKey,
  type StandardModule,
  type StandardTable,
} from '../recipes/recipeNewStandardModel.ts'

import {
  recipeLabwareTable,
  recipeReagentRacks,
  recipeReagentTable,
  type ReagentRackView,
  recipeSampleTable,
  recipeSampleNames,
  recipeSampleTrayCells,
} from './recipeComposition.ts'
import { recipeTemplateStageTable } from './templateModel.ts'

import '../screens.css'
import '../resource.css'
import '../monitoring/supplyPanel.css'

/**
 * 레시피 문서 한 벌의 본문 — 템플릿 › 레시피 상세(`RecipeTemplateDetail`)와 「＋ 레시피」 창(`RecipeNewStandardForm`)이 같이 쓴다
 * (eric 2026-10-13 「레서피 추가도 마찬가지로.. 지금 레서피 상세 보기 형태로」).
 *
 * ```
 * [트레이 그림 — 시료 자리 · 묶음 색]
 * ┌ (분류 · 명령 수) ───────────────────────────┐
 * │ 원심분리기  기본값(한 줄씩 · 추가 창은 입력 칸)  › 스텝 57 │
 * │ 어셈블러    기본값                          › 스텝 101│
 * │ 염색기      기본값  › 모듈 스텝  › 작업 차례  › 랩웨어  › 시약 │
 * └─────────────────────────────────────────────┘
 * ```
 * 🔴 값은 레시피 문서 · 서버 요약 그대로다. 다른 템플릿(작업 · 데크)과 잇거나 견주지 않는다 — 그 템플릿 화면은 걷었다(eric 2026-10-26 「응.. 다 빼줘」).
 */
export function RecipeDocBody({
  doc,
  summary,
  headRows,
  fields,
  common,
  inputs,
  samples = true,
}: {
  /**
   * 장비 묶음 카드 맨 앞 묶음들(원심분리기 위) — 「레시피 추가」 창의 「이름」 · 「트레이」(eric 2026-10-20 「공통은 이름으로 하고.. 밑에 트레이를..
   * 트레이라고 타이틀을 붙여줘」). 묶음마다 제목 · 내용. 안 넘기면 없다
   */
  common?: readonly { key: string; title: string; node: ReactNode }[] | undefined
  /**
   * 레시피 안의 사용자 값 입력 칸(「레시피 추가」 창 · eric 2026-10-20 「사용자가 입력하는 정하는 부분이 레서피에 잇다며.. 그거 넣어줘봐」) —
   * 칸 열쇠(`stage.<단계>.<volume|rpm|seconds>` · `reagent.<id>.<lot|volume|dead>`)와 접근 이름을 받아 입력을 돌려준다. 그 칸이 문서에
   * 없으면 `undefined`(글자 그대로). 안 넘기면 읽기 화면이다
   */
  inputs?: ((key: string, label: string) => ReactNode | undefined) | undefined
  doc: RecipeV2Doc
  summary: RecipeV2Summary
  /** 시료 자리 트레이를 맨 위에 세우나 — 「레시피 추가」 창은 끈다(그 창은 「공통」 묶음 안에 따로 세운다 · `SampleTray bare`) */
  samples?: boolean | undefined
  /** 맨 위 카드 머리 줄(분류 · 명령 수 등) */
  headRows?: readonly { key: string; label: string; value: ReactNode }[] | undefined
  /** 장비 기본값 자리를 입력 칸으로(추가 창) — 안 넘기면 읽기(한 줄씩) */
  fields?: ((module: StandardModule, place?: 'stages') => ReactNode) | undefined
}) {
  return (
    <div className="fstack">
      {/* 시료 자리 — 맨 위 · 제목 없이 트레이 그림만(eric 2026-10-13 「시료 자리 5를 맨 위로.. 시료자리5 이 텍스트는 빼주고」) */}
      {samples ? <SampleTray doc={doc} samples={summary.samples} /> : null}
      {/*
            🔴 맨 위 카드 = 레시피 문서의 장비별 묶음(원심분리기 · 어셈블러 · 염색기 — 기본값 · 스텝 · 염색기 작업 차례). 제목을 달지 않는다 —
               「레시피 템플릿에만 있는 것」 같은 설명 글을 적지 않는다(eric 2026-10-13)
          */}
      <Card>
        {headRows === undefined || headRows.length === 0 ? null : <ValueRows rows={headRows} />}
        {(common ?? []).map((group) => (
          <div className="rtpl__group" data-module={group.key} key={group.key}>
            <h4 className="rtpl__grouptitle">{group.title}</h4>
            {group.node}
          </div>
        ))}
        {/* 장비별 기본값 — 장비 소제목 아래 한 줄에 하나씩(eric 2026-10-13 「각각의 메트릭은 한줄씩」) */}
        {MODULES.map((module) => {
          const rows = defaultRows(doc, module)
          const stageRows = defaultRows(doc, module, 'stages')
          const steps = moduleStepTable(doc, module).rows.length
          // 염색기는 작업 차례 · 배치가 있으면 기본값이 없어도 선다
          const empty =
            rows.length === 0 &&
            steps === 0 &&
            !(module === 'staining' && summary.stages.length > 0)
          return empty ? null : (
            <div className="rtpl__group" data-module={module} key={module}>
              <h4 className="rtpl__grouptitle">{t(MODULE_LABEL[module])}</h4>
              {/* 랩웨어 — 염색기 제목 바로 아래 · 펼친 채(eric 2026-10-13 「랩웨어도.. 염색기 쪽에.. 펼쳤다 접었다」 · 2026-10-20 「랩웨어7을 염색기 바로 아래로 올려줘」) */}
              {module === 'staining' ? (
                <SummaryTable
                  bare
                  /* 접지 않는다(eric 2026-10-20 「랩웨어는 펼친 상태로 보이게 해줘」) */
                  /*
                   * 데크 그림(eric 2026-10-20 「랩웨어 부분인데.. 이것도 그림으로 그려줘」) — 실물 데크 정의 좌표. 표 왼쪽에 줄여 세운다
                   * (eric 2026-10-21 「랩웨어 그림이 너무 커.. 크기 좀 줄여주고.. 그 그림 오른쪽에.. 랩웨어 위치 및 순서」) — 원심 · 어셈블러와 같은 그림 | 값 두 열
                   */
                  lead={<RecipeDeckFigure doc={doc} />}
                  leadBeside
                  centerEmpty
                  countOf={(rows) => rows.filter((row) => (row.cells['name'] ?? '') !== '').length}
                  part="labware"
                  table={recipeLabwareTable(doc)}
                  title={t('tplTable.labware')}
                />
              ) : null}
              {/* 시약 — 랩웨어 다음 · 기본값보다 위(eric 2026-10-20 「염색기의 가장 위쪽에 넣어줘. 기본 팁 정책 이거보다 더 위에」 → 랩웨어가 그 위로) */}
              {module === 'staining' ? (
                <ReagentSection doc={doc} inputs={inputs} summary={summary} />
              ) : null}
              {/* 기본값 — 상세는 읽기(한 줄씩), 추가 창은 그 자리에 입력 칸(`fields`) · 원심분리기는 왼쪽에 장비 그림 */}
              <ModuleFigureRow module={module}>
                {fields !== undefined ? (
                  fields(module)
                ) : rows.length === 0 ? null : (
                  <ValueRows rows={rows} />
                )}
              </ModuleFigureRow>
              {/*
                    염색기 — 작업 차례(eric 2026-10-13 「작업 차례가 염색기 내용이잖아. 염색기 부분에 넣어줄래?」)
                  */}
              {module === 'staining' ? (
                <SummaryTable
                  /* 표 위 — 기본 액체 클래스(표의 빈 「액체 클래스」 단계가 쓰는 값 · eric 2026-10-20 「위치가 애매한데」 → 「응 해봐」) */
                  lead={
                    fields !== undefined ? (
                      fields(module, 'stages')
                    ) : stageRows.length === 0 ? null : (
                      <ValueRows rows={stageRows} />
                    )
                  }
                  bare
                  /* 접지 않는다 · 모듈 스텝 위(eric 2026-10-20 「작업 차례 13을 위로 올려줘 모듈 스텝 위로.. 작업 차례는 항상 보이게」) */
                  part="stages"
                  table={stageWorkTable(withDefaultLiquid(recipeTemplateStageTable(summary), doc))}
                  /* 부피 · 세이킹 속도 · 시간은 추가 창에서 입력 칸(반복 횟수는 스텝 수라 보기만) */
                  {...(inputs === undefined
                    ? {}
                    : {
                        cellInput: (row: StandardTable['rows'][number], column: string) =>
                          column === 'volume' || column === 'rpm' || column === 'seconds'
                            ? inputs(stageKey(row.key, column), `${row.key} ${column}`)
                            : undefined,
                      })}
                  title={t('recipeTemplate.stageList')}
                />
              ) : null}
              {/*
                    그 장비의 스텝(원심 57 · 어셈블러 101 · 염색기 장비 스텝 12) — 장비 묶음 안(eric 2026-10-13 「스텝 57 이거를.. 위쪽 원심분리기
                    어셈블러 염색기 위에 넣어줘」). 종전 아래 탭 카드는 걷었다. 염색 분주 스텝은 작업 차례를 펼친 것이라 따로 세우지 않는다
                  */}
              {steps === 0 ? null : (
                <SummaryTable
                  bare
                  /* 스텝은 많아(원심 57 · 어셈블러 101) 처음엔 접어 둔다(eric 2026-10-13 「스텝은 줄였다 펼쳤다.. 디폴트로 안보이게」) */
                  folded
                  part={`steps-${module}`}
                  /* 열이 9개라 창에 다 안 든다 — 핵심 열만 세우고 나머지(시퀀스 값 · 비고 · 출발 · 도착 …)는 줄을 눌러 펼친다 */
                  primary={STEP_PRIMARY}
                  table={moduleStepTable(doc, module)}
                  /* 세 장비 모두 「모듈 스텝」 — 같은 칸(`process.<장비>.module_steps`)이라 이름도 하나(eric 2026-10-20 「통일 시키는 것이 낫지 않나?」) */
                  title={t('recipeEdit.moduleSteps')}
                />
              )}
            </div>
          )
        })}
      </Card>
    </div>
  )
}

/**
 * 시료 자리 — 모니터링 시료 서랍과 같은 트레이 그림(`TrayFigure` `chamberOutline` · eric 2026-10-13 「시료자리는 모니터링의 그림을
 * 이용해서」). 칸 가운데에 자리 이름(P1 · REF), 빈 칸은 점선. 웰 이름이 규격 밖이면 그림 대신 표.
 */
export function SampleTray({
  samples,
  doc,
  bare = false,
}: {
  samples: readonly { sample_ref: string; wells: readonly string[] }[]
  /** 레시피 문서 — 자리 이름(`liquids[자리].displayName`)을 오른쪽 목록에 적는다. 없으면 자리 · 칸만 */
  doc?: RecipeV2Doc | undefined
  /** 카드 없이 — 「레시피 추가」 창의 「공통」 묶음 안에 선다(eric 2026-10-20 「공통에는.. 시료 내용을 여기다가 넣어줘」) */
  bare?: boolean
}) {
  const cells = recipeSampleTrayCells(samples, TRAY_SLOTS)
  const title = t('recipeTemplate.samples')
  const names = doc === undefined ? {} : recipeSampleNames(doc)
  const body =
    cells === undefined ? (
      <SummaryTable bare part="samples" table={recipeSampleTable(samples)} title={title} />
    ) : (
      <div className="rtpl__tray" data-part="samples">
        <TrayFigure
          cells={cells}
          chamberOutline
          label={samples.map((s) => `${s.sample_ref} ${s.wells.join(', ')}`).join(' · ')}
        />
        {/* 트레이 오른쪽 자리 목록 — 자리 · 이름 · 칸(eric 2026-10-20 「트레이 오른쪽에. 간단하게 저거 정리해줄래?」) */}
        <dl className="rtpl__traykey">
          {samples.map((s) => (
            <div key={s.sample_ref}>
              <dt>{s.sample_ref}</dt>
              <dd>
                {names[s.sample_ref] === undefined ? null : (
                  <span className="rtpl__trayname">{names[s.sample_ref]}</span>
                )}
                <span className="rtpl__traywells num">{s.wells.join(' · ')}</span>
              </dd>
            </div>
          ))}
        </dl>
      </div>
    )
  return bare ? body : <Card>{body}</Card>
}

/**
 * **시약 배치 그림** — 시약이 실린 랙마다 모니터링 소모품 카드와 같은 랙 그림(`DeckRackFigure` + 칸 이름 축 `RackAxesFrame`) 한 장과
 * 그 아래 번호 · 이름 · 칸 목록(eric 2026-10-20 「이것... 실제 그림으로부터 그려줘」).
 *
 * ```
 * Tube rack 1        Tube rack 2        Cold rack
 * [① ①]              [② ○]  [   ]       [⑤ ⑥ ⑦ ○]
 * [① ④]              [③ ○]  [   ]       [⑤ ⑥ ⑦ ○] …
 * 1 워싱용액 A1·A2·A3  2 고정용액 A1       5 1차 항체 용액 A1·B1
 * ```
 * 🔴 계획이지 상태가 아니다 — 시약이 실릴 칸만 튜브를 그리고 나머지는 빈 구멍이다(장비 잔량과 무관).
 * 🔴 번호 = 시약 표의 줄 차례(`recipeReagentRacks`) — 색만으로 시약을 가르지 않는다. 칸 글자는 번호, 이름은 아래 목록이 말한다.
 * 그림 종류 · 랙 이름은 모니터링 데크 표(`deckRacksOf('stain')`) 하나를 읽는다 — 표에 없는 자리는 그림 없이 표만 남는다.
 */
/**
 * **시약 묶음** — 늘 펼쳐 둔다(eric 2026-10-20 「이거 항상 보이게해주고... 아랫부분... 저거 여러 줄은.. 없애고. 웰 양을.. 해당 rack에 넣어줘」).
 * 랙 그림 + 랙마다 번호 · 이름 · 칸 · 양 목록이 표를 대신한다. 그릴 랙이 하나도 없으면(데크 표에 없는 자리) 종전 표로 선다 — 값을 숨기지 않는다.
 */
function ReagentSection({
  doc,
  summary,
  inputs,
}: {
  doc: RecipeV2Doc
  summary: RecipeV2Summary
  inputs?: ((key: string, label: string) => ReactNode | undefined) | undefined
}) {
  const samples = new Set(summary.samples.map((x) => x.sample_ref))
  const table = recipeReagentTable(doc, samples)
  const drawn = drawableRacks(recipeReagentRacks(doc, samples))
  if (drawn.length === 0)
    return <SummaryTable bare folded part="reagents" table={table} title={t('tplTable.reagent')} />
  return (
    <div className="rtpl__sub" data-part="reagents">
      <h5 className="rtpl__subtitle">
        {t('tplTable.reagent')}{' '}
        <span className="rtpl__count">{table.rows.length.toLocaleString(intlLocale())}</span>
      </h5>
      <ReagentRacks inputs={inputs} racks={drawn} />
    </div>
  )
}

/** 그릴 수 있는 시약 랙 — 모니터링 데크 표(`deckRacksOf('stain')`)에 있는 튜브 · 냉장 랙만(그림 종류 · 이름을 거기서 읽는다) */
function drawableRacks(racks: readonly ReagentRackView[]) {
  const deck = deckRacksOf('stain')
  return racks.flatMap((rack) => {
    const known = deck.find((entry) => sameLabware(entry.labwareId, rack.labwareId))
    return known === undefined || known.kind === 'tcstock' || known.kind === 'tips'
      ? []
      : [{ ...rack, kind: known.kind, name: known.aria() }]
  })
}

function ReagentRacks({
  racks: drawn,
  inputs,
}: {
  racks: ReturnType<typeof drawableRacks>
  /** 추가 창 — 시약마다 양 · lot · 남김 입력(`RecipeDocBody` 의 `inputs`) */
  inputs?: ((key: string, label: string) => ReactNode | undefined) | undefined
}) {
  return (
    <div className="rtpl__racks" data-part="reagent-racks">
      {drawn.map((rack) => {
        const wells = rack.reagents.flatMap((reagent) => reagent.wells)
        const marks = Object.fromEntries(
          rack.reagents.flatMap((reagent) => reagent.wells.map((w) => [w, String(reagent.no)])),
        )
        return (
          <figure
            aria-label={rack.name}
            className="rtpl__rack"
            data-labware={rack.labwareId}
            key={rack.labwareId}
          >
            {/* 랙 한 줄 = 왼쪽 그림 | 오른쪽 시약 목록 · 입력(eric 2026-10-20 「세개의 행으로 하고.. 각각의 행의 왼쪽에는 그림.. 오른쪽에는 입력 창」) */}
            <div className="rtpl__rackfig">
              <p aria-hidden="true" className="rtpl__rackname">
                {rack.name}
              </p>
              <RackAxesFrame definition={rack.definition} kind={rack.kind}>
                <DeckRackFigure
                  definition={rack.definition}
                  kind={rack.kind}
                  label={`${rack.name} — ${rack.reagents
                    .map((r) => `${String(r.no)} ${r.name} ${r.wells.join(', ')}`)
                    .join(' · ')}`}
                  marks={marks}
                  occupancy={{
                    [REST_CELLS]: 'used',
                    ...Object.fromEntries(wells.map((w) => [w, 'full' as const])),
                  }}
                  reagents={Object.fromEntries(wells.map((w) => [w, null]))}
                />
              </RackAxesFrame>
            </div>
            <ol className="rtpl__racklist">
              {rack.reagents.map((reagent) => (
                <li key={reagent.no}>
                  <span className="rtpl__rackno num">{reagent.no}</span>
                  <span className="rtpl__rackreagent">{reagent.name}</span>
                  <span className="rtpl__rackwells num">{reagent.amount}</span>
                  {inputs === undefined ? null : (
                    <ReagentInputs id={reagent.id} inputs={inputs} name={reagent.name} />
                  )}
                </li>
              ))}
            </ol>
          </figure>
        )
      })}
    </div>
  )
}

/** 시약 한 줄의 입력 — 부피(칸마다 같을 때) · 남김 · lot. 문서에 없는 칸은 안 선다 */
function ReagentInputs({
  id,
  name,
  inputs,
}: {
  id: string
  name: string
  inputs: (key: string, label: string) => ReactNode | undefined
}) {
  const parts = (
    [
      /* 부피 · 남김 · lot 차례(eric 2026-10-20 「부피, 남김, lot 순으로 해줘」) */
      ['volume', t('recipeEdit.f.volumeUl')],
      ['dead', t('recipeEdit.f.deadVolumeUl')],
      ['lot', t('column.lot')],
    ] as const
  ).flatMap(([part, label]) => {
    const node = inputs(reagentKey(id, part), `${name} ${label}`)
    return node === undefined ? [] : [{ part, label, node }]
  })
  if (parts.length === 0) return null
  return (
    <span className="rtpl__rackinputs">
      {parts.map(({ part, label, node }) => (
        <label className="rtpl__rackinput" data-part={part} key={part}>
          <span className="rtpl__rackinputlabel">{label}</span>
          {node}
        </label>
      ))}
    </span>
  )
}

/**
 * 장비 묶음의 기본값 줄 — 원심분리기 · 어셈블러는 왼쪽에 작은 장비 그림, 오른쪽에 값(eric 2026-10-20 「원심분리기에.. 장비쪽 원심분리기 사진 작게
 * 간단하게 표시하고. 오른쪽에... 값 입력하는 거 넣어줘봐. 롤백할 수 있어. 봐서..」 — 시험 삼아 · 이 커밋 하나로 되돌린다).
 * 그림은 모니터링 장비 판과 같은 로터(`RotorFigure`) · 어셈블러 정면도(`AssemblerStationLine`)이고 비어 있다 — 레시피에는 적재 상태가 없다. 염색기는 종전 그대로
 */
function ModuleFigureRow({ module, children }: { module: StandardModule; children: ReactNode }) {
  /* 어셈블러도 같은 꼴(eric 2026-10-20 「어셈블러도 넣어줘봐」) — 모니터링 장비 판과 같은 그림 · UV 꺼짐 · 빈 스테이지 */
  const figure =
    module === 'centrifuge' ? (
      <RotorFigure
        label={t('module.cent')}
        size="lg"
        slots={([1, 2, 3, 4] as const).map((index) => ({ index, loaded: false }))}
      />
    ) : module === 'assembler' ? (
      /* 장비 판과 같은 정면도(압착 + UV 스테이지 — eric 2026-10-20 「엇메블러는. 장비쪽 어셈블러.. 장비로 해줘」 · 「TC rack과 폐기통은 없애줘」). 레시피라 멈춘 그림 */
      <AssemblerStationLine
        /* 장비 판처럼 그림 안 그리퍼는 그리지 않는다 */
        gripper={false}
        label={t('module.asm')}
        phase="idle"
        statusLabel=""
        uvOn={false}
      />
    ) : module === 'staining' ? (
      /* 염색기는 그림 없이 빈 자리 — 값은 원심분리기 · 어셈블러와 같은 오른쪽 열에 선다(eric 2026-10-20 「그림만 없애줘」) */
      <span />
    ) : null
  if (figure === null) return children
  return (
    <div className="rtpl__withfig" data-module-figure={module}>
      <div aria-hidden="true" className={`rtpl__modfig rtpl__modfig--${module}`}>
        {figure}
      </div>
      <div className="rtpl__withfig-body">{children}</div>
    </div>
  )
}

/** 모듈 스텝 표에 세우는 열 — 스텝 ID · 시퀀스 · 옮기는 것 · 회차 · 챔버(짧은 값). 그 밖은 줄 펼침(eric 2026-10-20 「핵심 열 + 펼침」) */
const STEP_PRIMARY: readonly string[] = ['key', 'sequence', 'payload_kind', 'round', 'chambers']

/** 장비 셋 — 레시피 템플릿에만 있는 공정 기본값 · 장비 스텝 */
const MODULES = ['centrifuge', 'assembler', 'staining'] as const satisfies readonly StandardModule[]

const MODULE_LABEL = {
  centrifuge: 'module.cent',
  assembler: 'module.asm',
  staining: 'module.stain',
} as const

/** 그 장비의 공정 기본값 — 칸마다 한 줄(레시피 추가 창의 칸 정의 `STANDARD_FIELDS` · 이름 그대로) */
function defaultRows(doc: RecipeV2Doc | undefined, module: StandardModule, place?: 'stages') {
  const values = initialValues(doc)
  return fieldsOf(doc, module, place).map((f) => ({
    key: fieldId(f),
    label: t(f.label),
    value: values[fieldId(f)] ?? '',
  }))
}

/**
 * **작업 차례 표 읽기 쉽게** — 이름(`gel_dpbs`) · 모양(`exchange_shake`) · 시약 역할 · 반복 횟수 네 열을 「작업」 한 열로 합친다:
 * 「워싱용액 교체 · 흔들기 ×3」(시약 + 모양 한글 + 반복). 원래 이름은 그 칸 아래 작은 글자로 남는다.
 * 모양 한글은 서버 모양 어휘 넷(`fill` · `fill_shake` · `exchange` · `exchange_shake`)의 번역이다 — 모르는 모양은 원문 그대로.
 * 폐액 분류는 원문(`biohazard`) 그대로(eric 2026-10-20 「biohazard 그대로 써줘」).
 */
const SHAPE_LABEL: Readonly<Record<string, MessageKey>> = {
  fill: 'recipeTemplate.shape.fill',
  fill_shake: 'recipeTemplate.shape.fill_shake',
  exchange: 'recipeTemplate.shape.exchange',
  exchange_shake: 'recipeTemplate.shape.exchange_shake',
}

function stageWorkTable(table: StandardTable): StandardTable {
  const merged = new Set(['name', 'shape', 'reagent_role', 'repeats'])
  const work: StandardTable['columns'][number] = { id: 'work', label: 'recipeTemplate.col.work' }
  const columns = table.columns.flatMap((c) =>
    c.id === 'name' ? [work] : merged.has(c.id) ? [] : [c],
  )
  return {
    columns,
    rows: table.rows.map((row) => {
      const shape = row.cells['shape'] ?? ''
      const shapeText = SHAPE_LABEL[shape] === undefined ? shape : t(SHAPE_LABEL[shape])
      const repeats = Number(row.cells['repeats'] ?? '')
      const work = [
        row.cells['reagent_role'] ?? '',
        shapeText,
        Number.isInteger(repeats) && repeats > 1 ? `×${String(repeats)}` : '',
      ]
        .filter((part) => part !== '')
        .join(' ')
      return { ...row, cells: { ...row.cells, work } }
    }),
  }
}

/**
 * 작업 차례 표의 빈 「액체 클래스」 칸 — 그 단계는 염색기 기본 액체 클래스를 쓴다. 「—」 대신 「water (기본)」으로 실제로 쓰이는 값을 보인다
 * (서버 방출기 `p.get("liquidClass") or ctx.default_class` 와 같은 규칙). 기본값이 없으면 종전 그대로(빈 칸 → 「—」)
 */
function withDefaultLiquid(table: StandardTable, doc: RecipeV2Doc): StandardTable {
  const fallback = defaultValue(doc, 'staining', 'default_liquid_class')
  if (fallback === undefined || !table.columns.some((c) => c.id === 'liquidClass')) return table
  const shown = t('recipeEdit.defaultLiquidCell', { name: fallback })
  return {
    ...table,
    rows: table.rows.map((row) =>
      (row.cells['liquidClass'] ?? '') === ''
        ? { ...row, cells: { ...row.cells, liquidClass: shown } }
        : row,
    ),
  }
}

/**
 * 카드 하나 = 표 하나 — 제목 옆에 줄 수(목록 칩과 같은 `count`). 빈 표는 「없음」 한 줄(표 머리만 세우지 않는다).
 * 🔴 열을 줄이지 않는다(`compact` — 좁으면 표가 가로로 민다) · 빈 칸은 「—」 · 긴 칸은 한 줄로 자르고 전문은 툴팁.
 */
function SummaryTable({
  title,
  table,
  part,
  lead,
  primary,
  cellInput,
  countOf,
  centerEmpty = false,
  bare = false,
  folded = false,
  leadBeside = false,
}: {
  /** 표 위(펼친 안)에 서는 것 — 시약 랙 그림 */
  lead?: ReactNode
  /**
   * `lead` 를 표 위가 아니라 표 왼쪽에 — 그림 | 표 두 열(`.rtpl__withfig` · 원심 · 어셈블러 줄과 같은 틀). 그림이 안 서면(데크를 못 받음) 표만 한 열로.
   * 묶음 안 · 펼친 표(`bare` · 접지 않음)에서만 쓴다
   */
  leadBeside?: boolean
  /**
   * 표에 세우는 열(열 id) — 주면 나머지 열은 줄을 눌러 그 아래에 펼친다(모듈 스텝 · eric 2026-10-20 「모듈 스텝이 오른쪽 짤리는데」 →
   * 「핵심 열 + 펼침」). 안 주면 열이 다 선다
   */
  primary?: readonly string[] | undefined
  /** 제목 옆 수를 세는 법 — 안 주면 줄 수 */
  countOf?: ((rows: StandardTable['rows']) => number) | undefined
  /** 빈 칸의 「—」를 칸 가운데에(랩웨어 표의 빈 데크 칸 · eric 2026-10-20 「비어 있다는 표시로 - 중앙 정렬」) */
  centerEmpty?: boolean
  /** 칸을 입력으로 바꾼다(추가 창의 작업 차례) — `undefined` 를 돌리면 글자 그대로 */
  cellInput?:
    ((row: StandardTable['rows'][number], column: string) => ReactNode | undefined) | undefined
  /** 카드 없이(묶음 안 · 소제목 + 표) */
  bare?: boolean
  /** 접어 둔다 — 「스텝 57」 줄을 누르면 표가 펼쳐진다 */
  folded?: boolean
  title: string
  table: StandardTable
  part: string
}) {
  const [expanded, setExpanded] = useState<string | null>(null)
  /* 제목 옆 수 — 기본은 줄 수. 랩웨어 표는 빈 데크 칸 줄을 빼고 센다(실린 랩웨어 수 · eric 2026-10-20 「랩웨어가.. 7개 아니야?」) */
  const total = countOf?.(table.rows) ?? table.rows.length
  const count = <span className="rtpl__count">{total.toLocaleString(intlLocale())}</span>
  const shown =
    primary === undefined
      ? table.columns
      : table.columns.filter((column) => primary.includes(column.id))
  const more =
    primary === undefined ? [] : table.columns.filter((column) => !primary.includes(column.id))
  /** 그림 옆 반폭 표의 이름 칸 — 꺾어 다 보인다(자르지 않는다) */
  const wrapName = (id: string) => leadBeside && id === 'name'
  const label = (column: StandardTable['columns'][number]) =>
    column.label === undefined ? column.id : t(column.label)
  // 좁으면 이 상자 안에서만 가로로 민다 — 판 전체가 밀리지 않게 · 열 머리는 한 줄(글자 단위로 꺾이지 않게)
  const body = (
    <div className="rtpl__scroll" data-part={part}>
      <DataTable<StandardTable['rows'][number]>
        caption={title}
        {...(more.length === 0
          ? {}
          : {
              expandedKey: expanded,
              onToggleExpand: (key: string) => {
                setExpanded((now) => (now === key ? null : key))
              },
              /* 펼친 줄 — 표에 안 세운 열의 값(빈 칸은 뺀다). 긴 글은 여기서 줄바꿈된다 */
              renderExpanded: (row: StandardTable['rows'][number]) => {
                const filled = more.filter((column) => (row.cells[column.id] ?? '') !== '')
                return filled.length === 0 ? (
                  <p className="rtpl__more rtpl__more--none">{t('mask.none')}</p>
                ) : (
                  <dl className="rtpl__more">
                    {filled.map((column) => (
                      <div key={column.id}>
                        <dt>{label(column)}</dt>
                        <dd>{row.cells[column.id]}</dd>
                      </div>
                    ))}
                  </dl>
                )
              },
            })}
        columns={shown.map((column) => ({
          key: column.id,
          header: label(column),
          /*
           * 랩웨어 이름(낱말 글)만 칸 안에서 꺾는다 — 나머지(식별자 · 수 · 짧은 값)는 한 줄. 그림 옆 반폭 표(`leadBeside` · 랩웨어)의 「랩웨어」 칸도
           * 꺾는다 — 한 줄이면 긴 랩웨어 이름(「Opentrons 10 Tube Rack with Falcon 4x50 mL, 6x15 mL Conical」)이 반폭을 넘어 표가 가로로 밀린다
           */
          nowrap: column.id !== 'displayName' && !wrapName(column.id),
          cell: (row) => {
            const input = cellInput?.(row, column.id)
            if (input !== undefined) return input
            const text = row.cells[column.id] ?? ''
            if (text === '')
              return centerEmpty ? (
                <span className="rtpl__emptycell">{t('mask.none')}</span>
              ) : (
                t('mask.none')
              )
            /*
             * 「작업」 칸 — 무엇을 · 어떻게(「워싱용액 교체 · 흔들기 ×3」) 아래 작은 글자로 원래 단계 이름(`gel_dpbs`)
             * (eric 2026-10-20 「이름이나.. 모양쪽.. 너무 이해하기 어려운데」 → 「한번 해봐봐」)
             */
            if (column.id === 'work') {
              const name = row.cells['name'] ?? ''
              return (
                <span className="rtpl__work">
                  <span>{text}</span>
                  {name === '' ? null : <span className="rtpl__workname">{name}</span>}
                </span>
              )
            }
            if (column.id === 'displayName' || wrapName(column.id)) return text
            return (
              <span className="clip" title={text}>
                {text}
              </span>
            )
          },
        }))}
        density="compact"
        emptyLabel={t('mask.none')}
        rowKey={(row) => row.key}
        rows={table.rows}
      />
    </div>
  )
  if (folded) {
    const disclosure = (
      <Disclosure label={title} meta={total.toLocaleString(intlLocale())}>
        {lead}
        {body}
      </Disclosure>
    )
    // 묶음 안이면 소제목 자리, 따로면 카드 한 장 — 둘 다 「› 제목 수」 한 줄로 접혀 선다
    return bare ? <div className="rtpl__sub">{disclosure}</div> : <Card>{disclosure}</Card>
  }
  if (bare)
    return (
      <div className="rtpl__sub" data-part={`${part}-section`}>
        <h5 className="rtpl__subtitle">
          {title} {count}
        </h5>
        {leadBeside ? (
          <div className="rtpl__withfig rtpl__withfig--lead">
            <div className="rtpl__modfig rtpl__modfig--lead">{lead}</div>
            <div className="rtpl__withfig-body">{body}</div>
          </div>
        ) : (
          <>
            {lead}
            {body}
          </>
        )}
      </div>
    )
  return (
    <Card title={title} titleEnd={count}>
      {body}
    </Card>
  )
}
